import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter } from 'rxjs/operators'
import { TranslateService } from '@ngx-translate/core'

import { GoRealtimeClient } from '@core/services/realtime/go-realtime.client'
import { WebAlert } from '@core/modules/toolbar/notifications/web-alert'
import { NotificationMessage } from '@core/modules/toolbar/notifications/notification-message.model'
import { ApiService } from '@shared/services/api.service'
import { SnackBarService } from '@core/modules/snack-bar/snack-bar.service'
import { SettingsService } from '@app/shared/common-components/settings/settings.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { EventLog } from '@shared/modules/alerts/shared/event-log.model'
import { Vehicle } from '@shared/models/vehicle.model'
import { NotificationAcknowledgeStatus } from '@shared/models/notification-acknowledge.model'

import { SosAlert, SOS_EVENT_NAME } from './sos-alert.model'
import { mapEventLogToSos } from './sos-event-log'

/**
 * Persistent SOS/panic surface. Root singleton, first injected by ToolbarComponent (post-login),
 * so the JWT is available. Live panic frames arrive over the realtime v3 native WS: the shared
 * GoRealtimeClient demuxes them onto `alert$` as flat WebAlerts carrying the `event` name, so we
 * filter `event === 'PANIC_BUTTON'` there (RealtimeAlertService.normalizeWebAlert drops the name).
 */
@Injectable({ providedIn: 'root' })
export class SosService {
  private readonly _alerts$ = new BehaviorSubject<SosAlert[]>([])
  readonly alerts$: Observable<SosAlert[]> = this._alerts$.asObservable()

  private soundEnabled = false

  get alerts(): SosAlert[] {
    return this._alerts$.value
  }

  constructor(
    private readonly realtimeClient: GoRealtimeClient,
    private readonly apiService: ApiService,
    private readonly snackBar: SnackBarService,
    private readonly translate: TranslateService,
    private readonly settingsService: SettingsService,
    private readonly authService: AuthService,
  ) {
    this.listenForSos()
    this.loadActive()
    this.hydrateSoundSetting()
  }

  // Public so a synthetic alert can be pushed in dev/testing; production callers use the socket.
  ingest(raw: WebAlert): void {
    if (raw?.event !== SOS_EVENT_NAME) return
    const alert = this.fromWebAlert(raw)
    if (this._alerts$.value.some((a) => a.id === alert.id)) return
    this._alerts$.next([alert, ...this._alerts$.value])
    this.enrich(alert)
    this.resolveVehicle(alert)
    this.playAlarm()
  }

  acknowledge(id: number, note = ''): void {
    const alert = this._alerts$.value.find((a) => a.id === id)
    if (!alert?.acknowledgeId) {
      this.remove(id) // no acknowledge record => clear locally only
      return
    }
    this.apiService
      .post(`/notifications/acknowledge/${alert.acknowledgeId}`, {
        status: NotificationAcknowledgeStatus.Actioned,
        comment: note,
      })
      .subscribe({
        next: () => this.remove(id),
        error: () => this.snackBar.notificationArrived(this.translate.instant('SOS.ACK_FAILED') as string),
      })
  }

  private listenForSos(): void {
    this.realtimeClient.connect() // idempotent — ensures the shared WS is up even without the map/bell
    this.realtimeClient.alert$.pipe(filter((raw: WebAlert) => raw?.event === SOS_EVENT_NAME)).subscribe((raw: WebAlert) => this.ingest(raw))
  }

  private loadActive(): void {
    this.apiService.getPageable<NotificationMessage>('/notifications/messages', 1, 100).subscribe({
      next: ({ data }) => {
        const active = (data || [])
          .filter((m) => m?.event?.eventName === SOS_EVENT_NAME)
          .filter((m) => this.isPendingAck(m))
          .map((m) => this.fromNotification(m))
        if (!active.length) return
        const existing = new Set(this._alerts$.value.map((a) => a.id))
        const fresh = active.filter((a) => !existing.has(a.id))
        if (!fresh.length) return
        this._alerts$.next([...fresh, ...this._alerts$.value])
        fresh.forEach((a) => {
          this.enrich(a)
          this.resolveVehicle(a)
        })
      },
      error: () => {
        /* live socket still populates */
      },
    })
  }

  private enrich(alert: SosAlert): void {
    if (!alert.eventLogId) return
    this.apiService.getPageable<EventLog>('/event-log/json', 1, 1, undefined, { id: alert.eventLogId }).subscribe({
      next: ({ data }) => {
        const eventLog = (data || [])[0]
        if (!eventLog) return
        const patch = Object.fromEntries(
          Object.entries(mapEventLogToSos(eventLog)).filter(([, value]) => value != null),
        ) as Partial<SosAlert>
        this._alerts$.next(this._alerts$.value.map((a) => (a.id === alert.id ? { ...a, ...patch } : a)))
      },
      error: () => {
        /* keep notification-derived fields */
      },
    })
  }

  private resolveVehicle(alert: SosAlert): void {
    if (alert.vehicleId != null) return
    const name = alert.vehicle?.trim()
    if (!name) return
    const fields = ['regNo', 'defaultLabel', 'device.trackerData']
    this.apiService.getPageable<Vehicle>('/vehicles/fields', 0, 10, undefined, { regNo: name }, fields).subscribe({
      next: ({ data }) => {
        const vehicles = data || []
        const exactMatch = vehicles.find((v) => v.regNo === name || v.defaultLabel === name)
        const match = exactMatch ?? (vehicles.length === 1 ? vehicles[0] : undefined)
        if (!match) return
        const patch: Partial<SosAlert> = { vehicleId: match.id }
        const coordinates = match.device?.trackerData?.lastCoordinates
        if (coordinates?.lat != null && +coordinates.lat !== 0 && +coordinates.lng !== 0) {
          patch.lat = +coordinates.lat
          patch.lng = +coordinates.lng
        }
        this._alerts$.next(this._alerts$.value.map((a) => (a.id === alert.id ? { ...a, ...patch } : a)))
      },
      error: () => {
        /* leave vehicleId unset (button hidden) */
      },
    })
  }

  private remove(id: number): void {
    this._alerts$.next(this._alerts$.value.filter((a) => a.id !== id))
  }

  private isPendingAck(message: NotificationMessage): boolean {
    return !!message.acknowledge && message.acknowledge.status !== NotificationAcknowledgeStatus.Actioned
  }

  private fromWebAlert(raw: WebAlert): SosAlert {
    return {
      id: Number(raw.id),
      driver: raw.subject || 'SOS alert',
      vehicle: this.parseVehicleName(raw.subject, raw.body) ?? '',
      location: raw.body || '',
      occurrenceTime: this.toOccurrenceTime(raw.ts),
      message: raw.body,
      acknowledgeId: undefined, // live frame carries no acknowledge id
      eventId: raw.eventId ?? undefined,
      eventLogId: raw.eventLogId,
      eventTeam: raw.entityTeam?.type,
      vehicleId: raw.vehicleId,
    }
  }

  private fromNotification(message: NotificationMessage): SosAlert {
    return {
      id: Number(message.id),
      driver: message.subject || 'SOS alert',
      vehicle: this.parseVehicleName(message.subject, message.message) ?? '',
      location: message.message || '',
      occurrenceTime: message.occurrenceTime,
      message: message.message,
      acknowledgeId: message.acknowledge?.id,
      eventId: message.event?.eventId,
      eventLogId: message.event?.eventLogId,
      eventTeam: message.event?.entityTeam?.type,
      vehicleId: message.event?.vehicleId,
    }
  }

  private parseVehicleName(subject?: string, body?: string): string | undefined {
    const source = subject || body || ''
    const matched = source.match(/vehicle:\s*(.+?)\s*\.?\s*$/i)
    return matched?.[1]?.trim() || undefined
  }

  private toOccurrenceTime(ts: string | number | undefined): string {
    if (ts == null || ts === '') return ''
    return typeof ts === 'number' ? new Date(ts).toISOString() : ts
  }

  private hydrateSoundSetting(): void {
    const userId = this.authService.getCurrentUser()?.id
    if (!userId) return
    this.settingsService.getUserNotificationSoundSetting(userId).subscribe((setting) => (this.soundEnabled = !!setting?.value))
  }

  private playAlarm(): void {
    if (!this.soundEnabled) return
    const audio = new Audio('/assets/audio/juntos-607.mp3')
    audio.volume = 1
    audio.play().catch(() => {
      /* autoplay may be blocked until a user gesture */
    })
  }
}
